import { v4 as uuidv4 } from "uuid";
import { setSessionId } from "../Actions/ActionCreators";
import store from "../Store/Store";

export const convertStringToPascalCase = (inputString) => {
  let words = inputString.split(/(\s|\(|\[|\)|\])/);

  return words
    .map((word, index) => {
      if (word.trim() === "" || ["(", "[", ")", "]"].includes(word)) {
        return word;
      }

      return word.length > 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word;
    })
    .join("");
};

export const handlePortionSizeCount = (sizeCount, fieldName, setFieldValue) => {
  if (sizeCount === "" || /^\d{1,3}$/.test(sizeCount)) {
    setFieldValue(fieldName, sizeCount);
  }
};

export const handleFormattedInputChange = ({
  event,
  fieldName,
  formatterFunction,
  setFieldValue,
}) => {
  const inputElement = event.target;
  const cursorPosition = inputElement.selectionStart;
  const rawValue = event.target.value.replace(/ +/g, " ");
  const formattedValue = formatterFunction(rawValue);
  const positionAdjustment = formattedValue.length - rawValue.length;
  setFieldValue(fieldName, formattedValue);
  setTimeout(() => {
    inputElement.setSelectionRange(
      cursorPosition + positionAdjustment,
      cursorPosition + positionAdjustment
    );
  }, 0);
};

export function getSessionId() {
  const state = store.getState();
  const existingSessionId = state.sessionReducer?.sessionId;
  if (existingSessionId) {
    return existingSessionId;
  }

  const newSessionId = uuidv4();
  store.dispatch(setSessionId(newSessionId));
  return newSessionId;
}

export function getTransactionId() {
  return uuidv4();
}

export function logUserAction(action, params = {}) {
  if (window.newrelic) {
    window.newrelic.addPageAction(action, {
      sessionId: getSessionId(),
      transactionId: getTransactionId(),
      ...params,
    });
  }
}

export function logError(error, params = {}) {
  if (window.newrelic) {
    window.newrelic.noticeError(error, {
      sessionId: getSessionId(),
      transactionId: getTransactionId(),
      ...params,
    });
  }
}

export function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const obfuscatedSep =
  /(?:\s*(?:\[\s*(?:dot|dt)\s*]|\(\s*(?:dot|dt)\s*\)|[\s.-]|\b(?:dash|minus)\b)\s*)/
    .source;
const obfuscatedAt =
  /(?:\s*(?:\[\s*(?:at|@)\s*]|\(\s*(?:at|@)\s*\)|@|\s+at\s+)\s*)/.source;
const obfuscatedDot =
  /(?:\s*(?:\[\s*(?:dot|\.)\s*]|\(\s*(?:dot|\.)\s*\)|\.|\s+dot\s+)\s*)/.source;

export { obfuscatedSep, obfuscatedAt, obfuscatedDot };

const numberWords = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "oh",
  "double",
  "triple",
];
const numberWordPattern = `(?:${numberWords.join("|")})`;

export { numberWords, numberWordPattern };

const commonTLDs = [
  "com",
  "org",
  "net",
  "edu",
  "gov",
  "io",
  "co",
  "uk",
  "ca",
  "de",
  "fr",
  "au",
  "info",
  "biz",
  "me",
  "ly",
  "ee",
  "ai",
  "app",
  "dev",
  "tech",
  "xyz",
  "online",
  "store",
  "shop",
  "in",
  "linktr\\.ee",
];
const tldPattern = `(?:${commonTLDs.join("|")})`;

export { commonTLDs, tldPattern };

export const PII_PHONE_REGEX = new RegExp(
  [
    "\\b\\d{10,15}\\b",
    "\\(\\d{3}\\)\\s?\\d{3}-\\d{4}\\b",
    "\\b\\d{3}[-.]\\d{3}[-.]\\d{4}\\b",
    "\\+\\d{1,3}\\s?\\d{1,4}\\s?\\d{3,4}\\s?\\d{3,4}\\b",
    `\\b\\d{3}${obfuscatedSep}\\d{3}${obfuscatedSep}\\d{4}\\b`,
    `\\b(?:${numberWordPattern}(?:\\s+|${obfuscatedSep.replace(
      /\\s\*/g,
      "\\s*"
    )})){6,}${numberWordPattern}\\b`,
  ].join("|"),
  "gi"
);

export const PII_EMAIL_REGEX = new RegExp(
  [
    "\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z]{2,}\\b",
    `\\b[A-Za-z0-9._%+-]+${obfuscatedAt}[A-Za-z0-9.-]+${obfuscatedDot}[A-Za-z]{2,}\\b`,
  ].join("|"),
  "gi"
);

export const PII_URL_SOCIAL_REGEX = new RegExp(
  [
    "https?:\\/\\/(?:www\\.)?[\\w\\.-]+\\.[a-zA-Z]{2,}(?:\\/[\\w\\.\\-~:/?#\\[\\]@!$&'()*+,;=]*)?",
    "www\\.[\\w\\.-]+\\.[a-zA-Z]{2,}(?:\\/[\\w\\.\\-~:/?#\\[\\]@!$&'()*+,;=]*)?",
    `\\b[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]${obfuscatedDot}${tldPattern}\\b(?:\\/[\\w\\.\\-~:/?#\\[\\]@!$&'()*+,;=]*)?`,
    "@(?:[\\w._-]*[\\w_])",
    `(?:(?:instagram|facebook|twitter|x|tiktok|youtube|linkedin|youtu\\.be)${obfuscatedDot}com|(?:youtu\\.be)|linktr\\.ee)(?:\\/[\\w\\.\\-~:/?#\\[\\]@!$&'()*+,;=@%]*)*`,
  ].join("|"),
  "gi"
);

export const PII_ADDRESS_REGEX = new RegExp(
  [
    "\\b(?:Apartment|Apt\\.?|Suite|Ste\\.?|Unit|Unt\\.?|Flat|Room|Rm\\.?|#)\\s+[A-Za-z0-9-]+\\b",
    "\\b\\d{1,5}(?:\\s?[A-Za-z])?\\s+(?:[A-Za-z0-9'().-]+\\s+){1,7}(?:(?:(?:Apartment|Apt\\.?|Suite|Ste\\.?|Unit|Unt\\.?|Floor|Fl\\.?|Room|Rm\\.?|Flat|Building|Bldg\\.?|Tower|Block|Sector|#)\\s?[A-Za-z0-9-]+)|(?:Street|St\\.?|Road|Rd\\.?|Lane|Ln\\.?|Avenue|Ave\\.?|Boulevard|Blvd\\.?|Drive|Dr\\.?|Court|Ct\\.?|Place|Pl\\.?|Square|Sq\\.?|Terrace|Ter\\.?|Parkway|Pkwy\\.?|Circle|Cir\\.?|Way|Crescent|Cres\\.?|Heights|Hts\\.?|Trail|Trl\\.?|Crossing|Xing\\.?|Causeway|Cswy\\.?|Expressway|Expy\\.?|Freeway|Fwy\\.?|Highway|Hwy\\.?|Junction|Jct\\.?|Plaza|Plz\\.?|Point|Pt\\.?|Run|View|Walk|Close|Gardens|Gdns\\.?|Grove|Grv\\.?|Mews|Parade|Pde\\.?|Row|Vale|Villas|Yard|Phase|Colony|Nagar))\\b",
    "\\bP(?:ost(?:al)?)?\\.?\\s*O(?:ffice)?\\.?\\s*Box\\s+[A-Za-z0-9-]+\\b",
    "\\b\\d{5}(?:-\\d{4})?\\b",
    "\\b[A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d\\b",
    "\\b[A-Z]{1,2}\\d[A-Z\\d]?\\s*\\d[A-Z]{2}\\b",
    "\\b[A-Za-z\\s.,'-]+?(?:,\\s*|\\s+)(?:[A-Z]{2,}|[A-Za-z.]{2,})(?:\\s+|,\\s*)(?:\\d{5}(?:-\\d{4})?|[A-Z]\\d[A-Z]\\s*\\d[A-Z]\\d|[A-Z]{1,2}\\d[A-Z\\d]?\\s*\\d[A-Z]{2})\\b",
  ].join("|"),
  "gi"
);

export const CODE_REGEX = /<[^>]+>|{[^}]+}|function\s+\w+|\bconsole\.log\b/i;
